import { getRequiredSlots } from '../gphSkillRegistry';

const GAME_ALIASES = {
  pubg: 'pubg_mobile',
  pubgm: 'pubg_mobile',
  'pubg mobile': 'pubg_mobile',
  freefire: 'free_fire',
  'free fire': 'free_fire',
  ff: 'free_fire',
  cod: 'codm',
  codm: 'codm',
  'cod mobile': 'codm',
  'call of duty mobile': 'codm',
  'call of duty: mobile': 'codm'
};

const SUPPORTED_GAMES = new Set(['pubg_mobile', 'free_fire', 'codm', 'other']);

const stripAccents = (value = '') =>
  String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const cleanToken = (value = '') => stripAccents(value).replace(/[^a-z0-9:_]+/g, ' ').trim();

const normalizeGame = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const cleaned = cleanToken(raw);
  return GAME_ALIASES[cleaned] || raw;
};

const toStringList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .slice(0, 6);
};

const normalizeSymptomReport = (value) => {
  if (typeof value === 'string') {
    return {
      primary_symptoms: [],
      secondary_symptoms: [],
      timing: null,
      severity: 'medium',
      free_text: value.trim()
    };
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      primary_symptoms: [],
      secondary_symptoms: [],
      timing: null,
      severity: 'medium',
      free_text: ''
    };
  }

  return {
    primary_symptoms: toStringList(value.primary_symptoms || value.primarySymptoms || value.symptoms),
    secondary_symptoms: toStringList(value.secondary_symptoms || value.secondarySymptoms),
    timing: value.timing ? String(value.timing).trim().slice(0, 40) : null,
    severity: value.severity ? String(value.severity).trim().slice(0, 40) : 'medium',
    free_text: String(value.free_text || value.freeText || value.text || '').trim().slice(0, 500)
  };
};

const hasSymptomSignal = (symptomReport = {}) => (
  Boolean(symptomReport.free_text)
  || (Array.isArray(symptomReport.primary_symptoms) && symptomReport.primary_symptoms.length > 0)
  || (Array.isArray(symptomReport.secondary_symptoms) && symptomReport.secondary_symptoms.length > 0)
);

const toPositiveNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export const normalizeSensiIaSlots = (slots = {}) => {
  const normalized = { ...slots };
  normalized.game = normalizeGame(normalized.game);
  normalized.symptomReport = normalizeSymptomReport(
    normalized.symptomReport || normalized.symptom_report || normalized.report
  );
  normalized.weapon = normalized.weapon ? String(normalized.weapon).trim().slice(0, 80) : null;
  normalized.scope = normalized.scope ? String(normalized.scope).trim().slice(0, 60) : null;
  normalized.gyroMode = normalized.gyroMode ? String(normalized.gyroMode).trim().slice(0, 60) : null;
  normalized.distance = normalized.distance ? String(normalized.distance).trim().slice(0, 60) : null;
  normalized.device = normalized.device ? String(normalized.device).trim().slice(0, 80) : null;
  normalized.fps = toPositiveNumber(normalized.fps);
  normalized.fingers = toPositiveNumber(normalized.fingers);
  normalized.currentSettings = (
    normalized.currentSettings
    && typeof normalized.currentSettings === 'object'
    && !Array.isArray(normalized.currentSettings)
  ) ? normalized.currentSettings : {};
  return normalized;
};

export const validateSensiIaSlots = (slots = {}, mode = 'report_only') => {
  const normalizedSlots = normalizeSensiIaSlots(slots);
  const required = getRequiredSlots('sensi_ia', mode);
  const missingSlots = [];
  const invalidSlots = [];

  if (Array.isArray(required) && required.includes('game') && !normalizedSlots.game) {
    missingSlots.push('game');
  }

  if (Array.isArray(required) && required.includes('symptomReport') && !hasSymptomSignal(normalizedSlots.symptomReport)) {
    missingSlots.push('symptomReport');
  }

  if (normalizedSlots.game && !SUPPORTED_GAMES.has(normalizedSlots.game)) {
    invalidSlots.push({
      slot: 'game',
      value: normalizedSlots.game,
      reason: 'unsupported_game'
    });
  }

  if (mode !== 'report_only') {
    invalidSlots.push({
      slot: 'mode',
      value: mode,
      reason: 'mode_not_implemented_in_mi_chat'
    });
  }

  return {
    valid: missingSlots.length === 0 && invalidSlots.length === 0,
    normalizedSlots,
    missingSlots,
    invalidSlots
  };
};

export const buildMissingSensiIaSlotsMessage = (missingSlots = []) => {
  const missing = new Set(missingSlots);
  if (missing.has('game') && missing.has('symptomReport')) {
    return 'Me diz o jogo e o que esta acontecendo com a sua mira para eu diagnosticar pela Sensi-IA.';
  }
  if (missing.has('game')) {
    return 'Qual jogo voce quer ajustar: PUBG Mobile, Free Fire ou COD Mobile?';
  }
  if (missing.has('symptomReport')) {
    return 'O que esta acontecendo com sua mira? Pode escrever do seu jeito: sobe, treme, abre spray, passa do alvo ou fica pesada.';
  }
  return 'Preciso de mais um detalhe para diagnosticar sua sensibilidade.';
};

export const buildInvalidSensiIaSlotsMessage = (invalidSlots = []) => {
  const first = invalidSlots[0];
  if (!first) return null;

  if (first.reason === 'mode_not_implemented_in_mi_chat') {
    return 'Consigo diagnosticar pela Sensi-IA com seu relato agora. Para analise com video, me envie o video pelo fluxo de upload da Mi.';
  }

  if (first.reason === 'unsupported_game') {
    return 'Esse jogo ainda nao esta claro para a Sensi-IA. Me diga PUBG Mobile, Free Fire ou COD Mobile.';
  }

  return 'Nao consegui validar esses dados para a Sensi-IA. Me diga o jogo e o sintoma da mira.';
};

export default validateSensiIaSlots;
